// TileView — renders a single tile as a 2-layer 2.5D structure: span.tile (ivory BODY, styled by
// theme.css via the --tile-s custom property) > .tile-face (the FACE: amenoasa SVG, atlas crop, or
// glyph placeholder). It consumes the A1 atlas manifest when one is available (cropping the atlas
// image to the face's frame rect), and otherwise falls back to a readable glyph placeholder
// (spec §6.1 ExampleHandRenderer: placeholder until `studio:atlas-changed` promotes real art).
// The component is atlas-or-svg-or-placeholder by construction; all three ride the same .tile body.
//
// Styling contract (P2 design §8 / §3.3): the ONLY inline style on the body is the `--tile-s`
// injection (unitless number; theme.css derives width = calc(var(--tile-s) * 1px), height ×1.36,
// radius ×0.12, face inset ×0.06). Look — bevel, side-thickness shadows, aka corner ribbon
// (data-aka="1" ::after), state lifts (.tile-btn, added by CALLERS) — all live in theme.css.
import { type CSSProperties } from 'react';
import type { TileId } from '@mj/protocol';
import { baseOfAka } from '@mj/protocol';
import type { AtlasManifest } from '@mj/assets';
import { tileSvgUrl } from '@mj/assets/tiles';

// Unicode-ish readable glyphs for the placeholder. Honors use their kanji; suited tiles show the
// rank + a suit mark. Aka (red 5) keeps its red text tint; the body ribbon comes from data-aka.
const HONOR_GLYPH: Record<string, string> = {
  z1: '東', z2: '南', z3: '西', z4: '北', z5: '白', z6: '發', z7: '中',
  z8: '⑧風', z9: '⑨風',
};
// 花牌 (春夏秋冬 / 梅蘭菊竹) — the SVG path renders these normally; this is the text fallback only.
const FLOWER_GLYPH: Record<string, string> = {
  f1: '春', f2: '夏', f3: '秋', f4: '冬', f5: '梅', f6: '蘭', f7: '菊', f8: '竹',
};
const SUIT_MARK: Record<string, string> = { m: '萬', p: '筒', s: '索' };

export interface TileViewProps {
  tile: TileId;
  size?: number;
  manifest?: AtlasManifest | null;
  atlasBaseUrl?: string;
  title?: string;
  /** 選択中 (2 タップ打牌の 1 タップ目など) → data-selected="1"。描画専用・既定 off。 */
  selected?: boolean;
  /** 聴牌アシスト金枠パルス → data-tenpai="1" (theme.css fx-tenpai-pulse)。 */
  tenpai?: boolean;
  /** 最終打牌の白リング → data-last="1" (D3)。 */
  last?: boolean;
  /** 打牌不可など → aria-disabled (theme.css が減彩)。クリック抑止は呼び出し側の責務。 */
  disabled?: boolean;
  /** 追加クラスのパススルー。interactive な持ち上げは呼び出し側が `tile-btn` を付ける。 */
  className?: string;
}

function placeholderFace(tile: TileId): { text: string; aka: boolean } {
  const base = baseOfAka(tile);
  const aka = tile.endsWith('r');
  if (base[0] === 'z') return { text: HONOR_GLYPH[base] ?? base, aka };
  if (base[0] === 'f') return { text: FLOWER_GLYPH[base] ?? base, aka };
  const rank = base.slice(1);
  const mark = SUIT_MARK[base[0]!] ?? base[0]!;
  return { text: `${rank}${mark}`, aka };
}

export function TileView({
  tile, size = 36, manifest, atlasBaseUrl = '', title,
  selected, tenpai, last, disabled, className,
}: TileViewProps) {
  // Geometry the FACE crop math needs locally. The body itself is sized by theme.css from
  // --tile-s (width S / height S×1.36 / face inset S×0.06) — we never duplicate that inline.
  const inset = Math.round(size * 0.06); // 現行 SVG パスの padding と同じ丸め
  const faceW = size - inset * 2;
  const faceH = Math.round(size * 1.36) - inset * 2;

  // Shared body attributes — every branch (atlas / svg / placeholder) rides the same span.tile.
  // data-tile stays on the OUTER element (tests / dev tooling query it). data-aka="1" lets
  // theme.css draw the warm body + corner ribbon ::after (赤5 の非色手掛かり, §9.5).
  const aka = tile.endsWith('r'); // m5r / p5r / s5r のみ
  const bodyClass = className ? `tile ${className}` : 'tile';
  const bodyStyle = { '--tile-s': size } as CSSProperties; // ← inline はこの注入のみ
  const bodyProps = {
    className: bodyClass,
    style: bodyStyle,
    title: title ?? tile,
    'data-tile': tile,
    'data-aka': aka ? '1' : undefined,
    'data-selected': selected ? '1' : undefined,
    'data-tenpai': tenpai ? '1' : undefined,
    'data-last': last ? '1' : undefined,
    'aria-disabled': disabled || undefined,
  } as const;

  // ---- atlas path: if a manifest frame exists for this face, crop the atlas image. The crop
  // background math is runtime data (frame rect) so it CANNOT live in theme.css — the face keeps
  // explicit px dims matching the body's inset box so the crop stays self-consistent. ----
  const frame = manifest?.frames.find((f: AtlasManifest['frames'][number]) => f.id === baseOfAka(tile));
  if (manifest && frame) {
    const img = manifest.atlas['1x'];
    const scaleX = faceW / frame.rect.w;
    const scaleY = faceH / frame.rect.h;
    const faceStyle: CSSProperties = {
      width: faceW,
      height: faceH,
      backgroundImage: `url(${atlasBaseUrl}${img.png})`,
      backgroundPosition: `-${frame.rect.x * scaleX}px -${frame.rect.y * scaleY}px`,
      backgroundSize: `${img.width * scaleX}px ${img.height * scaleY}px`,
      display: 'inline-block',
    };
    return (
      <span {...bodyProps} data-atlas="1">
        <span className="tile-face" style={faceStyle} />
      </span>
    );
  }

  // ---- real-art path: render the vendored amenoasa SVG for this TileId. Keyed by the FULL id so
  // aka fives (m5r → Man5-Dora) show their red face, distinct from the plain five. Ids with no
  // vendored art (z8/z9) fall through to the placeholder below. The amenoasa faces are transparent
  // (face-only); the ivory body behind them is now .tile in theme.css. ----
  const svg = tileSvgUrl(tile);
  if (svg) {
    return (
      <span {...bodyProps} data-svg="1">
        <img className="tile-face" src={svg} alt={tile} draggable={false} />
      </span>
    );
  }

  // ---- placeholder path (no atlas, no vendored art). Text styling is face-local and not part of
  // theme.css's tile contract, so it stays inline (existing-codebase convention: inline + var()). ----
  const { text, aka: akaText } = placeholderFace(tile);
  const faceStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: akaText ? '#c0202a' : '#1d2b1d',
    fontSize: Math.round(size * 0.42),
    fontWeight: 700,
    lineHeight: 1,
    userSelect: 'none',
    fontFamily: 'var(--font-ui, system-ui, sans-serif)',
  };
  return (
    <span {...bodyProps} data-atlas="0">
      <span className="tile-face" style={faceStyle}>{text}</span>
    </span>
  );
}

export function TileRow({ tiles, size = 36, manifest = null }: { tiles: TileId[]; size?: number; manifest?: AtlasManifest | null }) {
  return (
    <span style={{ display: 'inline-flex', gap: 2, flexWrap: 'wrap' }}>
      {tiles.map((t, i) => (
        <TileView key={`${t}-${i}`} tile={t} size={size} manifest={manifest} />
      ))}
    </span>
  );
}
